import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';

import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { MEDIDASPRODUCTOS, MedidasProductosPagingRes } from '@/shared/interfaces/erp/inventario';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchMedidasProductos = ({
  page = 1,
  page_size = 10,
  nombre = '',
}: GetMedidasProductosParams) => {
  return useQuery({
    queryKey: ['unidad-medida', page, nombre, page_size],
    queryFn: async () => {
      return getMedidasProductos({ page, nombre, page_size });
    },
  });
};

export const useGetMedidaProducto = (id: string) => {
  return useQuery({
    queryKey: ['unidad-medida', id],
    queryFn: async () => {
      return getMedidaProducto(id);
    },
  });
};

export const useCreateMedidaProducto = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createMedidaProducto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unidad-medida'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Medida de producto creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateMedidaProducto = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateMedidaProducto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unidad-medida'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Medida de producto actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetMedidasProductosParams = Partial<MEDIDASPRODUCTOS> & {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreateMedidasProductosParams = Omit<MEDIDASPRODUCTOS, 'id_medida_producto'>;
export type UpdateMedidasProductosParams = {
  id: string;
  data: CreateMedidasProductosParams;
};

export const getMedidasProductos = async (params?: GetMedidasProductosParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<MedidasProductosPagingRes>(
    `${urlBackend}api/v1/unidad-medida/?${queryParams}`,
    true
  );
};

export const getMedidaProducto = async (id: string) => {
  try {
    return await get<MEDIDASPRODUCTOS>(`${urlBackend}api/v1/unidad-medida/${id}/`, true);
  }
  catch (error) {
    return handleAxiosError(error);
  }
};

export const createMedidaProducto = async (data: CreateMedidasProductosParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<MEDIDASPRODUCTOS>(`${urlBackend}api/v1/unidad-medida/`, data, true);
};

export const updateMedidaProducto = async ({ id, data }: UpdateMedidasProductosParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<MEDIDASPRODUCTOS>(`${urlBackend}api/v1/unidad-medida/${id}/`, data, true);
};
